import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import banner from "../assets/images/banner.jpg";
import hiw1 from "../assets/images/hiw-1.png";
import hiw2 from "../assets/images/hiw-2.png";
import hiw3 from "../assets/images/hiw-3.png";
import ServiceCard from "../components/ServiceCard";
import BlogCard from "../components/BlogCard";
import { useGetServicesQuery } from "../store/api/servicesApi";
import { useGetBlogsQuery } from "../store/api/blogsApi";
import { useGetFaqsQuery } from "../store/api/contentApi";

const WHATSAPP_NUMBER = "5997018199";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hi Contentour Bonaire! I'd like to know more about exploring the island."
)}`;

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white mb-2 p-4 rounded-[8px]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between"
      >
        <span className="font-manrope-semibold text-base text-black">
          {question}
        </span>
        <span className="text-secondary text-xl leading-none flex-shrink-0 ml-4">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <p className="font-manrope-regular text-base text-black pb-4 leading-relaxed">
          {answer}
        </p>
      )}
    </div>
  );
}

function Home() {
  const navigate = useNavigate();
  const { data: servicesData } = useGetServicesQuery();
  const featuredServices = (servicesData?.data || []).slice(0, 3);

  const { data: blogsData } = useGetBlogsQuery({ limit: 3 });
  const latestBlogs = blogsData?.data || [];

  const { data: faqsData } = useGetFaqsQuery();
  const homeFaqs = (faqsData?.data || []).slice(0, 6);

  return (
    <div className="bg-bg">
      {/* banner section */}
      <section
        className="relative min-h-[85vh] md:min-h-[90vh] flex items-center bg-cover bg-center"
        style={{ backgroundImage: `url(${banner})` }}
      >
        {/* Layered overlays: darken for contrast, vignette the edges, fade into the page below */}
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/70" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-bg" />

        <div className="relative w-full container mx-auto px-4 py-20 md:py-32 flex flex-col items-center text-center">
          {/* Eyebrow */}
          <span className="flex items-center gap-2 sm:gap-3 font-manrope-semibold text-[10px] sm:text-sm tracking-[0.18em] sm:tracking-[0.22em] uppercase text-white/80">
            <span className="h-px w-5 sm:w-8 bg-white/40" />
            Bonaire · Self-guided island tours
            <span className="h-px w-5 sm:w-8 bg-white/40" />
          </span>

          <h1 className="mt-6 text-white text-[42px] sm:text-[60px] md:text-[80px] lg:text-[96px] font-manrope-extrabold tracking-tight leading-[1.02]">
            Contentour Bonaire
          </h1>

          <span className="mt-6 block h-[3px] w-16 rounded-full bg-primary" />

          <p className="mt-7 max-w-2xl text-white text-lg sm:text-xl md:text-2xl font-manrope-medium leading-snug">
            Freedom to explore paradise in one day.
          </p>
          <p className="mt-3 max-w-xl text-white/75 text-sm sm:text-base md:text-lg font-manrope-light leading-relaxed">
            Guided by tech. Powered by nature. Electric rental car, your own guide
            and more.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-manrope-semibold text-base text-white bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 transition-colors px-6 py-3.5 rounded-full"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-5 h-5 fill-current"
                aria-hidden="true"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Chat
            </a>
            <button
              onClick={() => {
                const el = document.getElementById("services");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="w-full sm:w-auto font-manrope-semibold text-base text-black bg-white hover:bg-white/90 transition-colors px-9 py-3.5 rounded-full shadow-lg shadow-black/20"
            >
              Discover
            </button>
            <button
              onClick={() => navigate("/services")}
              className="w-full sm:w-auto font-manrope-semibold text-base text-white bg-primary hover:opacity-90 transition-opacity px-9 py-3.5 rounded-full"
            >
              Book
            </button>
          </div>
        </div>
      </section>

      {/* featured services section */}
      <div id="services" className="py-12 md:py-20 container mx-auto px-4">
        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
          <div className="flex flex-col gap-3">
            <span className="bg-success20 text-black font-manrope-semibold text-base px-4 py-1 rounded-full w-fit">
              Featured services
            </span>
            <h2 className="font-manrope-bold text-[32px] md:text-[40px] lg:text-[48px] text-black leading-tight">
              Find our featured services <br className="hidden sm:block" /> in one place.
            </h2>
          </div>
          <button
            onClick={() => navigate("/services")}
            className="font-manrope-semibold text-base text-black border border-border px-4 py-2 rounded-xl hover:text-black transition-colors sm:mt-2 w-fit"
          >
            See all
          </button>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredServices.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
      </div>

      {/* about us section */}
      <div id="about" className="py-12 md:py-20">
        <div className="container mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16 px-4">
          {/* Left: image collage */}
          <div className="w-full lg:flex-1 grid grid-cols-2 grid-rows-2 gap-4 h-[320px] md:h-[420px] lg:h-[480px]">
            <div className="row-span-2 rounded-2xl overflow-hidden">
              <img
                src={require("../assets/images/about-us-1.jpg")}
                alt="About 1"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src={require("../assets/images/about-us-2.jpg")}
                alt="About 2"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src={require("../assets/images/about-us-3.jpg")}
                alt="About 3"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right: content */}
          <div className="w-full lg:flex-1 flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <span className="bg-success20 text-black font-manrope-semibold text-base px-4 py-1 rounded-full w-fit">
                About us
              </span>
              <h2 className="font-manrope-bold text-[32px] md:text-[40px] lg:text-[48px] text-black leading-tight">
                A booking experience built around your schedule, not ours.
              </h2>
            </div>

            <div className="flex flex-col gap-6">
              {[
                {
                  number: "1.",
                  title: "Real-Time Precision",
                  description:
                    "Our system syncs instantly. What you see is exactly what's available, ensuring zero double-bookings and total accuracy for your plans.",
                },
                {
                  number: "2.",
                  title: "Bank-Grade Security",
                  description:
                    "We prioritize your privacy. All transactions are processed through encrypted gateways, supporting all major credit cards for a worry-free checkout.",
                },
                {
                  number: "3.",
                  title: "Instant Connectivity",
                  description:
                    "No more checking your email every five minutes. Get your booking confirmations, reminders, and updates sent directly to your WhatsApp.",
                },
                {
                  number: "4.",
                  title: "Complete Autonomy",
                  description:
                    "Plans change? We get that. Manage your entire history, reschedule appointments, or trigger automatic refunds directly from your personal dashboard.",
                },
              ].map(({ number, title, description }) => (
                <div key={number} className="flex flex-row gap-3">
                  <span className="font-manrope-bold text-[20px] text-black min-w-[14px]">
                    {number}
                  </span>
                  <div className="flex flex-col gap-1">
                    <p className="font-manrope-bold text-[20px] text-black">
                      {title}
                    </p>
                    <p className="font-manrope-regular text-base text-secondary leading-relaxed">
                      {description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* how it works section */}
      <div id="how-it-works" className="py-12 md:py-20">
        <div className="container mx-auto flex flex-col items-center gap-10 px-4">
          {/* Header */}
          <div className="flex flex-col items-center gap-4">
            <span className="bg-success20 text-black font-manrope-semibold text-base px-4 py-1 rounded-full w-fit">
              How it works
            </span>
            <h2 className="font-manrope-bold text-[32px] md:text-[40px] lg:text-[48px] text-black text-center leading-tight">
              Your Next Appointment, <br className="hidden md:block" /> Secured
              in Seconds.
            </h2>
          </div>

          {/* Steps */}
          <div className="flex flex-col md:flex-row items-center gap-4 w-full">
            {[
              {
                step: "Step one",
                title: "Discover",
                description:
                  "Browse our services and find exactly what you need.",
                bg: hiw1,
              },
              {
                step: "Step two",
                title: "Schedule",
                description:
                  "View our real-time calendar and grab a slot that fits your life.",
                bg: hiw2,
              },
              {
                step: "Step three",
                title: "Confirm",
                description:
                  "Pay securely and get an instant confirmation ping on Mail.",
                bg: hiw3,
              },
            ].map(({ step, title, description, bg }, index, arr) => (
              <React.Fragment key={step}>
                <div className="w-full md:flex-1 flex flex-col border border-gray-100 rounded-2xl overflow-hidden shadow-sm bg-white">
                  <div className="p-4 flex flex-col gap-1">
                    <span className="font-manrope-regular text-sm text-secondary">
                      {step}
                    </span>
                    <p className="font-manrope-bold text-[20px] text-black">
                      {title}
                    </p>
                    <p className="font-manrope-regular text-base text-secondary">
                      {description}
                    </p>
                  </div>
                  <img
                    src={bg}
                    alt={title}
                    className="w-full h-44 object-cover"
                  />
                </div>

                {index < arr.length - 1 && (
                  <div className="text-gray-300 flex-shrink-0 hidden md:block">
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 60 60"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        width="60"
                        height="60"
                        rx="30"
                        fill="#ffffff"
                        fill-opacity="0.5"
                      />
                      <rect
                        x="6"
                        y="6"
                        width="48"
                        height="48"
                        rx="24"
                        fill="#ffffff"
                      />
                      <path
                        d="M32.43 36.8201C32.24 36.8201 32.05 36.7501 31.9 36.6001C31.61 36.3101 31.61 35.8301 31.9 35.5401L37.44 30.0001L31.9 24.4601C31.61 24.1701 31.61 23.6901 31.9 23.4001C32.19 23.1101 32.67 23.1101 32.96 23.4001L39.03 29.4701C39.32 29.7601 39.32 30.2401 39.03 30.5301L32.96 36.6001C32.81 36.7501 32.62 36.8201 32.43 36.8201Z"
                        fill="black"
                      />
                      <path
                        d="M38.33 30.75H21.5C21.09 30.75 20.75 30.41 20.75 30C20.75 29.59 21.09 29.25 21.5 29.25H38.33C38.74 29.25 39.08 29.59 39.08 30C39.08 30.41 38.74 30.75 38.33 30.75Z"
                        fill="black"
                      />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* blog section */}
      {latestBlogs.length > 0 && (
        <div id="blog" className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4 flex flex-col gap-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex flex-col gap-3">
                <span className="bg-success20 text-black font-manrope-semibold text-base px-4 py-1 rounded-full w-fit">
                  From our blog
                </span>
                <h2 className="font-manrope-bold text-[32px] md:text-[40px] lg:text-[48px] text-black leading-tight">
                  Stories & travel insights.
                </h2>
              </div>
              <Link
                to="/blog"
                className="font-manrope-semibold text-base text-black border border-border px-4 py-2 rounded-xl hover:text-black transition-colors sm:mt-2 w-fit flex-shrink-0"
              >
                All articles
              </Link>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestBlogs.map((blog) => (
                <BlogCard key={blog.id || blog._id} blog={blog} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* faq section */}
      <div id="faq" className="py-12 md:py-20 bg-bg">
        <div className="container mx-auto flex flex-col lg:flex-row gap-10 lg:gap-16 px-4">
          {/* Left */}
          <div className="w-full lg:w-[35%] flex flex-col gap-4">
            <span className="bg-success20 text-black font-manrope-semibold text-base px-4 py-1 rounded-full w-fit">
              Frequently asked questions (FAQ)
            </span>
            <h2 className="font-manrope-bold text-[32px] md:text-[38px] lg:text-[42px] text-black leading-tight">
              Got questions? We have the answers.
            </h2>
          </div>

          {/* Right: accordion */}
          <div className="flex-1 flex flex-col">
            {homeFaqs.map((faq) => (
              <FAQItem key={faq.id || faq._id} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
